import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { describeSelection, PRESETS, toDateOnly, type PeriodSelection } from '../lib/period';
import { usePeriod } from '../lib/periodContext';

/**
 * Filtro de periodo do cabecalho (RNF15).
 *
 * Um botao que abre um menu com os presets em LINHAS (ninguem briga com um
 * calendario para pedir "ultimos 30 dias") e, atras de uma linha fina no
 * rodape, o intervalo personalizado com dois campos de data nativos.
 *
 * Fica no cabecalho, e nao dentro de cada cartao, porque o filtro escopa TUDO
 * abaixo dele: KPI, serie, funil, leads e campanhas mostram a mesma janela.
 * Dialogo simples, sem armadilha de foco de modal: fecha no Escape, no clique
 * fora e ao escolher.
 */
export function PeriodFilter(): JSX.Element {
  const { selection, setSelection } = usePeriod();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const onClick = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const choose = (next: PeriodSelection): void => {
    setSelection(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink shadow-soft transition-colors hover:border-edge hover:bg-elevated"
      >
        <CalendarGlyph />
        <span className="hidden sm:inline">{describeSelection(selection)}</span>
        <span className="sm:hidden">Período</span>
        <ChevronGlyph open={open} />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Escolher período"
          className="absolute right-0 z-drawer mt-2 w-72 overflow-hidden rounded-xl border border-line bg-elevated shadow-float animate-fade-in"
        >
          <ul className="py-1">
            {PRESETS.map((preset) => {
              const active = selection.kind === 'preset' && selection.preset === preset.value;
              return (
                <li key={preset.value}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => choose({ kind: 'preset', preset: preset.value })}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-ink/5 ${
                      active ? 'font-semibold text-ink' : 'text-ink-muted'
                    }`}
                  >
                    <span className="flex flex-col">
                      <span>{preset.label}</span>
                      <span className="text-xs font-normal text-ink-subtle">{preset.hint}</span>
                    </span>
                    {active ? <CheckGlyph /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <CustomRange selection={selection} onChoose={choose} />
        </div>
      ) : null}
    </div>
  );
}

function CustomRange({
  selection,
  onChoose,
}: {
  readonly selection: PeriodSelection;
  readonly onChoose: (next: PeriodSelection) => void;
}): JSX.Element {
  const hoje = toDateOnly(new Date());
  const [from, setFrom] = useState(selection.kind === 'custom' ? selection.from : hoje);
  const [to, setTo] = useState(selection.kind === 'custom' ? selection.to : hoje);
  const fromId = useId();
  const toId = useId();
  const invalido = from === '' || to === '' || from > to;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (invalido) {
      return;
    }
    onChoose({ kind: 'custom', from, to });
  };

  return (
    <form onSubmit={submit} className="border-t border-line px-4 py-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-subtle">Personalizado</p>
      <div className="grid grid-cols-2 gap-2">
        <label htmlFor={fromId} className="flex flex-col gap-1 text-xs text-ink-muted">
          De
          <input
            id={fromId}
            type="date"
            value={from}
            max={hoje}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink"
          />
        </label>
        <label htmlFor={toId} className="flex flex-col gap-1 text-xs text-ink-muted">
          Até
          <input
            id={toId}
            type="date"
            value={to}
            max={hoje}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink"
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={invalido}
        className="mt-3 h-9 w-full rounded-lg bg-brand text-sm font-medium text-white transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Aplicar período
      </button>
    </form>
  );
}

function CalendarGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="3.75" y="5.25" width="16.5" height="15" rx="2" />
      <path d="M3.75 9.75h16.5M8.25 3v4.5M15.75 3v4.5" />
    </svg>
  );
}

function ChevronGlyph({ open }: { readonly open: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={`h-4 w-4 text-ink-subtle transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-brand" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}
